import { AxiosResponse } from "axios";
import {
  contains,
  decoratorIsNotNullable,
  dynamicNamespace,
  equals,
  mergeFilters,
  select,
} from "utils/dynamic";
import {
  API,
  apiRtk,
  getByIDRequestHelper,
  makePatchArgs,
  rtkAdapterDynamicToSource,
  RTK_TAGS,
} from "utils/service";
import { PatchPartial } from "utils/types";
import { IDosageFormUnitType } from "__generated__/api";
import {
  IGridDosageFormUnitTypesParams,
  IGridDosageFormUnitTypesResponse,
} from "./models";

const dynamic = dynamicNamespace<IDosageFormUnitType>();
const REVALIDATE_KEY = RTK_TAGS.DOSAGE_FORM_UNIT_TYPE;

type SourceDosageFormUnitType = Pick<IDosageFormUnitType, "id"> & {
  title: string;
  countContent: number;
};

export const apiDosageFormUnitTypes = apiRtk.injectEndpoints({
  endpoints: (build) => ({
    getGridDosageFormUnitTypes: build.query<
      IGridDosageFormUnitTypesResponse,
      IGridDosageFormUnitTypesParams
    >({
      queryFn: async ({
        search,
        take: Take,
        skip: Skip,
        orderBy,
        isActive,
      }) => {
        try {
          const result = await API.api.dosageFormUnitTypesGetAllDynamicCreate({
            select: dynamic.select("id", "catalogName", "isActive"),
            filter: mergeFilters(
              dynamic.makeFilter(["catalogName"], search, contains),
              dynamic.makeFilter(
                "isActive",
                isActive,
                decoratorIsNotNullable(equals),
              ),
            ).join("&&"),
            orderBy: dynamic.orderBy(orderBy.field, orderBy.order),
            take: Take,
            skip: Skip,
            count: true,
          });
          return result;
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: [{ type: REVALIDATE_KEY }],
    }),
    getSourceDosageFormUnitTypes: build.query<
      Array<SourceDosageFormUnitType>,
      void
    >({
      queryFn: async () => {
        try {
          const res = await API.api.dosageFormUnitTypesGetAllDynamicCreate({
            select: select(
              "id",
              "catalogName as title",
              "dosageFormUnitTypeContents.Count as countContent",
            ),
            filter: dynamic.makeFilter("isActive", true, equals),
          });

          return rtkAdapterDynamicToSource(res) as AxiosResponse<
            Array<SourceDosageFormUnitType>
          >;
        } catch (error: any) {
          return { error };
        }
      },
    }),
    getDosageFormUnitType: build.query({
      queryFn: async (id: string) => {
        try {
          const result = await getByIDRequestHelper({
            apiFunc: API.api.dosageFormUnitTypesGetAllDynamicCreate,
            value: id,
          });
          return result;
        } catch (error: any) {
          return { error };
        }
      },
      providesTags: (_, __, id) => [{ type: REVALIDATE_KEY, id }],
    }),
    postDosageFormUnitType: build.mutation<
      IDosageFormUnitType,
      IDosageFormUnitType
    >({
      queryFn: async (data) => {
        try {
          const result = await API.api.dosageFormUnitTypesCreateCreate(data);
          return { data: result.data };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: REVALIDATE_KEY }],
    }),
    patchDosageFormUnitType: build.mutation<
      void,
      PatchPartial<IDosageFormUnitType, "id">
    >({
      queryFn: async (data) => {
        try {
          await API.api.dosageFormUnitTypesPatchPartialUpdate(
            ...makePatchArgs(data),
          );
          return { data: undefined };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: (_, __, data) => [
        { type: REVALIDATE_KEY },
        { type: REVALIDATE_KEY, id: String(data?.id) },
      ],
    }),
    deleteDosageFormUnitType: build.mutation<void, { id: string }>({
      queryFn: async (data) => {
        try {
          await API.api.dosageFormUnitTypesDeleteDelete(String(data?.id));
          return { data: undefined };
        } catch (error: any) {
          return { error };
        }
      },
      invalidatesTags: [{ type: REVALIDATE_KEY }],
    }),
  }),
});
